import XDate from 'xdate';
import {formatTimezoneOffset} from '~/common/utils';
import {
  selectSelectedBackground,
  selectSelectedEnvelope,
  selectSelectedLiner,
  selectSelectedStamp,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';
import {
  selectHostIds,
  selectPendingHostIds,
} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {updateEventIdInPathname} from '~/CreateFabricGreetingCard/utils/save';
import {SET_EVITE_EVENT} from '~/CreateFabricGreetingCard/reducers/eviteEvent/constants';
import {selectGlobalMessage} from '~/CreateFabricGreetingCard/reducers/recipientsMessage';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {selectUserOrSystemDisabled} from '~/common/fabric/GiftCard/reducers/eGiftCard';
// TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
// import {selectEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers/selector';
// import isEqual from 'lodash/isEqual';
// import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';

const now = () => {
  const future = new XDate();
  return future.toString('i').slice(0, 16) + formatTimezoneOffset();
};

const getTimezoneId = (name) => {
  try {
    return window.TIMEZONES.find((t) => t.timezone_display_name === name).timezone_id;
  } catch (e) {
    return name;
  }
};

export const stateToBackendEventRepresentation = (state) => {
  const {details, fabricTemplate, eviteEvent, fabricDesign, review, eGiftCard} = state;
  const background = selectSelectedBackground(state);
  const stamp = selectSelectedStamp(state);
  const liner = selectSelectedLiner(state);
  const envelope = selectSelectedEnvelope(state);
  const globalMessage = selectGlobalMessage(state);
  const egiftCardDisabled = selectUserOrSystemDisabled(state);

  const host_ids = selectHostIds(state);
  const pending_host_ids = selectPendingHostIds(state);
  const {guessing_games_data, sent_on, last_updated, date_created, rsvp, ...eventData} = eviteEvent;

  const sendLaterTimezone = evite.features.isEnabled('greeting_card_send_later_timezone', false);
  let amountInCents = 0;
  if (eGiftCard?.productConfigId && !eGiftCard.userDisabled) {
    amountInCents = eGiftCard.value;
  }
  const gift_card_data = {
    amount_in_cents: amountInCents,
    product_config_id: eGiftCard?.productConfigId || '',
    product_config_name: eGiftCard?.productConfigName || '',
    image: eGiftCard?.image || '',
    user_disabled: !!eGiftCard?.userDisabled,
  };
  const event = {
    ...eventData,
    host_ids,
    pending_host_ids,
    origin: 22,
    status: eviteEvent.status || 'draft',
    template_name: fabricTemplate.templateName,
    fabric_design_data: fabricDesign.fabricArt,
    card_back_text_content: globalMessage,
    phone_number: '',
    event_type: details.type,
    used_as: egiftCardDisabled ? 'card' : 'gift_card',
    gift_card_data,
    user_saved: true,
    cohost_name1: details.cohost.name,
    cohost_email1: details.cohost.email,
    location_name: details.location,
    street_address: details.address.street,
    city: details.address.city,
    state: details.address.state,
    zip_code: details.address.zip,
    title: details.title,
    message: details.notes,
    start_datetime: details.start
      ? XDate(details.start).toString('i').slice(0, 16) + formatTimezoneOffset()
      : now(),
    host_name: review.eventHostName || details.host,
    event_host_name: review.eventHostName || details.host,
    end_datetime:
      details.end && XDate(details.end).toString('i').slice(0, 16) + formatTimezoneOffset(),
    stop_datetime:
      details.stop && XDate(details.stop).toString('i').slice(0, 16) + +formatTimezoneOffset(),
    known_timezone_name:
      review.deliverCardNow || !sendLaterTimezone
        ? null
        : getTimezoneId(review.known_timezone_name),
    known_timezone_abbr:
      review.deliverCardNow || !sendLaterTimezone ? null : review.known_timezone_abbr,
    send_on: review.deliverCardNow
      ? null
      : XDate(review.deliveryCardDateTime).toString('i').slice(0, 16),
    gift_keywords: null,
    gift_list_url: null,
    wishlist: null,
    options: {
      ...eviteEvent.options,
      event_option_allow_invite_more: false, // This will no longer be supported in Fabric invitations
      // Remove rendered data so we don't overwrite what is on the server getting set asynchronously
      rendered_image_filename: undefined,
      rendered_image_gs_key: undefined,
      rendered_image_url: undefined,
      rendered_text_image_url: undefined,
      write: {text_layer_back: globalMessage},
    },
    is_fabric_premium: details.isPremium,
    pledgeling_new_api: true,
    // fields to not send back
    guessing_games_data: undefined,
    sent_on: undefined,
    last_updated: undefined,
    date_created: undefined,
    rsvp: undefined,
  };
  event.options.design = event.options.design || {};
  const {design} = event.options;
  if (envelope) {
    design.envelope = envelope.id;
    design.envelope_flap_down = envelope.flap_down_image;
    design.envelope_flap_up = envelope.blank;
    design.envelope_front = envelope.image;
    design.name_style = {
      // Text style for names on front of envelopes
      align: 'center',
      color: envelope.text_color,
      font: 'Financier',
      position: {x: 15, x2: 605, y: 225, y2: 465},
      size: 48,
    };

    if (liner) {
      design.liner = liner.id;
      for (const media of liner.media) if (media.label === 'image') design.liner_image = media.url;
    }
    if (stamp) {
      design.stamp = stamp.id;
      for (const media of stamp.media) if (media.label === 'image') design.stamp_image = media.url;
    }
    if (fabricDesign.backgroundId) {
      design.background = fabricDesign.backgroundId;
      for (const media of background.media)
        if (media.label === 'mobile') design.background_image_mobile = media.url;
      for (const media of background.media)
        if (media.label === 'desktop') design.background_image_desktop = media.url;
      design.background_color = null;
    } else {
      design.background = null;
      design.background_image_mobile = null;
      design.background_image_desktop = null;
      design.background_color = fabricDesign.backgroundColor;
    }
  }
  design.envelope_fabric_stickers = fabricDesign.envelopeStickers;
  if (!event.tracking_data)
    event.tracking_data = {gallery_category: details.tracking_gallery_category};

  window.saveUTMToObj(event);

  return event;
};

export const saveEventThunk =
  (saveToBackend = true, delayFetchSummary = 0) =>
  async (dispatch, getState) => {
    const state = getState();
    const eventId = state.eviteEvent.id;
    const {origin} = window.location;
    const url = eventId
      ? new URL(`/ajax/greeting-card/${eventId}/update`, origin)
      : new URL('/ajax/greeting-card/create', origin);

    const eventRepresentation = stateToBackendEventRepresentation(state);
    // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
    // const eventRepresentationSnapshot = selectEventToBackendSnapshot(getState());
    // const eventStateHasChanges = !eventRepresentationSnapshot
    //   ? true
    //   : !isEqual(eventRepresentation, eventRepresentationSnapshot);

    // if (saveToBackend && eventStateHasChanges) {
    if (saveToBackend) {
      let resp;
      try {
        resp = await evite.fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRFToken': evite.cookie('csrftoken'),
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(eventRepresentation),
        });
      } catch (e) {
        toastAlert('Failed to save card, please try again', {
          icon: <AlertLineIcon />,
          type: 'error',
          position: 'top-right',
        });
      }
      if (!resp.ok) {
        const {message} = await resp.json();
        evite.error('Failed to save eCard', new Error(message));
        toastAlert(message || 'Card could not be saved. Please try again or contact support.', {
          icon: <AlertLineIcon />,
          type: 'error',
          position: 'top-right',
        });
        // eslint-disable-next-line no-throw-literal
        throw 'Save failed';
      }
      const results = await resp.json();
      if (!eventId) updateEventIdInPathname(results.event.id);
      window.evite_event = results.event;
      dispatch({type: SET_EVITE_EVENT, payload: results.event});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //   setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    } else {
      window.evite_event = eventRepresentation;
      dispatch({type: SET_EVITE_EVENT, payload: eventRepresentation});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //     setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    }

    if (window.evite_event) {
      if (delayFetchSummary > 0) {
        setTimeout(async () => {
          await dispatch(fetchInvitationSummary(window.evite_event.id));
        }, delayFetchSummary);
      } else {
        await dispatch(fetchInvitationSummary(window.evite_event.id));
      }
    }
  };
